import React, { useEffect, useState, useContext } from "react";
import { PatientContext } from "../../lib/main-context";
import { Form, Col, Row } from "react-bootstrap";

// hooks
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers";
import { useHistory, useLocation } from "react-router-dom";
import { format } from "date-fns";

// components
import Sallie from "../UIComponents/Sallie";
import LabelledInput from "../UIComponents/LabelledInput";
import DescribeTBISchema from "./validations/describeTBISchema";
import SubmitButton from "../UIComponents/SubmitButton";
import OnboardingStepHeader from "./OnboardingStepHeader";

// api
import { createTBI } from "../../api/TBIRequests";
import customizeMessages from "../../lib/customizeMessages";

// translation
import { useTranslation } from "../../i18n/TranslationProvider";

const RecentTBI1 = ({ user, setOnboardingPercent }) => {
  const { patient } = useContext(PatientContext);
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const history = useHistory();
  const location = useLocation();

  const { register, handleSubmit, errors } = useForm({
    resolver: yupResolver(DescribeTBISchema),
  });

  useEffect(() => {
    location.state?.fromPatientControl
      ? setOnboardingPercent(30)
      : setOnboardingPercent(20);
    setLoading(false);
  }, []);

  const onSubmit = async (data) => {
    setSubmitting(true);
    try {
      data.dateOfTBI = format(new Date(data.dateOfTBI), "MM-dd-yyyy");
      await createTBI({ patientId: patient.patientId, data });
      setSubmitting(false);
      history.push("/RecentTBI2", {
        fromPatientControl: location.state?.fromPatientControl,
      });
    } catch (err) {
      console.log(err);
      history.push("/oops");
    }
  };

  const getPossibleMessages = () => ({
    mainHeader: {
      tbiPatient: t("recenttbi1.header.patient"),
      caregiver: t("recenttbi1.header.caregiver"),
    },
    sallieText: {
      tbiPatient: t("recenttbi1.sallie.patient"),
      caregiver: t("recenttbi1.sallie.caregiver"),
    },
    dateOfTbiLabel: {
      tbiPatient: t("recenttbi1.date.patient"),
      caregiver: t("recenttbi1.date.caregiver"),
    },
    addTbis: {
      tbiPatient: "You will have the option to add other TBI's after you set up your account.",
      caregiver: "You will have the option to add other TBI's after you set up your account.",
    },
    describeEventLabel: {
      tbiPatient: t("recenttbi1.describe.label.patient"),
      caregiver: t("recenttbi1.describe.label.caregiver"),
    },
    describeEventExtraInfo: {
      tbiPatient: t("recenttbi1.describe.info"),
      caregiver: t("recenttbi1.describe.info"),
    },
    describeEventPlaceholder: {
      tbiPatient: t("recenttbi1.describe.placeholder.patient"),
      caregiver: t("recenttbi1.describe.placeholder.caregiver"),
    },
    totalTbiLabel: {
      tbiPatient: t("recenttbi1.number.patient"),
      caregiver: t("recenttbi1.number.caregiver"),
    },
  });

  const getMessage = customizeMessages({ user, getPossibleMessages });

  return (
    <>
      {loading ? (
        t("common.loading")
      ) : (
        <>
          <div className="d-block">
            <OnboardingStepHeader text={getMessage("mainHeader")} />
            <Sallie text={getMessage("sallieText")} />
          </div>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <div className="d-block mt-4">
              <Form.Row>
                <Col>
                  <LabelledInput
                    autoFocus
                    required
                    labelAs="h6"
                    name="dateOfTBI"
                    placeholder="MM/DD/YYYY"
                    type="date"
                    date-format="mm/dd/yyyy"
                    inputRef={register}
                    label={getMessage("dateOfTbiLabel")}
                    errors={errors}
                  />
                </Col>
                <Col className="d-none align-items-end">
                  <div className="mb-3 ml-3">{getMessage("addTbis")}</div>
                </Col>
              </Form.Row>
              <LabelledInput
                inputAs="textarea"
                required
                labelAs="h6"
                extraInfo={getMessage("describeEventExtraInfo")}
                name="describeEvent"
                placeholder={getMessage("describeEventPlaceholder")}
                type="text"
                inputRef={register}
                label={getMessage("describeEventLabel")}
                errors={errors}
                rows={3}
              />
              <LabelledInput
                name="totalTBI"
                label={getMessage("totalTbiLabel")}
                labelAs="h6"
                required
                type="number"
                inputRef={register}
                errors={errors}
                min={1}
              />
              <Row>
                <SubmitButton type="submit" disabled={submitting}>
                  {t("onboarding.saveContinue")} &gt;
                </SubmitButton>
              </Row>
            </div>
          </Form>
        </>
      )}
    </>
  );
};

export default RecentTBI1;